import { Alert, Box } from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";

/**
 * The "you cannot edit your own permissions" notice shared by Account Access (FR-030) and
 * Feature Access (FR-036).
 *
 * The reason is **not** impersonation — it is that the page's subject is always self, so the rule
 * that applies is "a user cannot edit their own access permissions" (spec Clarifications). It is
 * therefore unconditional and does not vary between the ordinary and "as" cases.
 *
 * Copy comes from `labels.json` (`READ_ONLY_PERMISSIONS_NOTICE`) so all three locales resolve
 * through Label Services — **Configuration Principle VI** (Localized Labels — No Hardcoded UI
 * Text), FR-049. A tab needing different wording passes `message`, which must itself be a
 * localized string from `labels.json` — never a literal.
 */
export interface ReadOnlyNoticeProps {
  /**
   * Optional override for the shared notice, for a tab whose wording must differ. MUST be a
   * localized string; a raw literal here would bypass FR-049 and render untranslated in `fr-CA`.
   */
  message?: string;
  testId?: string;
}

/**
 * Rendered as an Interstate `Alert` (**Configuration Principle III** — Interstate &
 * Core-Components Only) with `role="status"` so assistive technology announces it. `status`
 * rather than `alert`: this is a standing property of the tab, not an error or a response to
 * something the user just did, so it should not interrupt.
 *
 * `type="caution"` — Interstate's name for the amber treatment the approved prototype uses here
 * (`alert-warning`); its `AlertSeverity` union has no `"warning"` member, and the prop's own doc
 * comment claiming otherwise is stale. Amber rather than `"info"` because this notice states a
 * **limit** on what the user can do, which is the one thing on these two tabs they might otherwise
 * spend time trying to work around. Every neighbouring notice on the page is informational blue, so
 * blue here made the constraint read as another aside.
 *
 * The notice is presentation only. It explains why the controls are inert; it does not enforce it.
 * The controls themselves carry `disabled`, and real authorization stays server-side
 * (**Monorepo Principle VII** — Security & Data Protection).
 */
export const ReadOnlyNotice = ({ message, testId }: ReadOnlyNoticeProps) => (
  <Box data-testid={testId ?? "read-only-notice"}>
    <Alert role={"status"} type={"caution"}>
      {message ?? (Locale.getText("READ_ONLY_PERMISSIONS_NOTICE") as string)}
    </Alert>
  </Box>
);
